import { useTemplateWithFields } from "@/hooks/useTemplateWithFields";
import type { TemplatesWithFields } from "@/integrations/supabase/templateEditor";
import debounce from "lodash.debounce";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";
import { BasicTemplateInfo } from "./forms/BasicTemplateInfo";
import { SearchTermsSection } from "./forms/SearchTermsSection";
import { TemplateFieldManager } from "./forms/TemplateFieldManager";
import { TemplateFormWrapper } from "./forms/TemplateFormWrapper";
import { useSearchTermsUpdateMutation, useTemplateUpdateMutation } from "./forms/TemplateMutations";

interface TemplateFormData {
  name: string;
}

interface TemplateFormProps {
  template: TemplatesWithFields[number];
  isLoading?: boolean;
}

const TemplateFormDumb = ({ template, isLoading }: TemplateFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const { templateId } = useParams();

  const updateTemplateMutation = useTemplateUpdateMutation();
  const updateSearchTermsMutation = useSearchTermsUpdateMutation();

  useEffect(() => {
    if (template) {
      setSearchTerms(template.template_search_terms?.map((term) => term.term) || []);
    }
  }, [template]);

  // Add beforeunload handler
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isSaving) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isSaving]);

  const debouncedSaveTemplateInfo = debounce(async (data: Partial<TemplateFormData>) => {
    setIsSaving(true);
    const toastId = toast.loading("Saving template...");
    try {
      await updateTemplateMutation.mutateAsync(data);
      toast.success("Template saved", { id: toastId });
    } catch (_error) {
      toast.error("Failed to save template", { id: toastId });
    } finally {
      setIsSaving(false);
    }
  }, 1000);

  const debouncedSaveSearchTerms = debounce(async (terms: string[]) => {
    setIsSaving(true);
    const toastId = toast.loading("Saving search terms...");
    try {
      await updateSearchTermsMutation.mutateAsync(terms);
      toast.success("Search terms saved", { id: toastId });
    } catch (_error) {
      toast.error("Failed to save search terms", { id: toastId });
    } finally {
      setIsSaving(false);
    }
  }, 1000);

  const handleFieldChange = (field: string, value: string) => {
    debouncedSaveTemplateInfo({ [field]: value });
  };

  const handleSearchTermsChange = (terms: string[]) => {
    setSearchTerms(terms);
    debouncedSaveSearchTerms(terms);
  };

  const onSubmit = async (data: TemplateFormData) => {
    setIsSubmitting(true);
    try {
      await updateTemplateMutation.mutateAsync(data);
      await updateSearchTermsMutation.mutateAsync(searchTerms);
      toast.success("Template updated successfully");
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to update template");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (templateId && isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-8">
      <TemplateFormWrapper onSubmit={onSubmit} isSubmitting={isSubmitting} templateId={template.id}>
        <BasicTemplateInfo onFieldChange={handleFieldChange} initialName={template?.name || ""} />

        <SearchTermsSection
          searchTerms={searchTerms}
          onAddTerm={({ term }) => handleSearchTermsChange([...searchTerms, term])}
          onRemoveTerm={(term) => handleSearchTermsChange(searchTerms.filter((t) => t !== term))}
        />

        <TemplateFieldManager templateId={template.id} />
      </TemplateFormWrapper>
    </div>
  );
};

export const TemplateForm = () => {
  const { template } = useTemplateWithFields();
  if (!template) return null;
  return <TemplateFormDumb template={template} />;
};
